// samplingEventForm/fieldNotes/actions.js

import { v4 as uuidv4 } from "uuid";
import { EmployeeFieldNotes } from "../../../../models/SamplingEvent/FieldNotes";
import { getTimestamp } from "../../../../features/samplingEventForm/samplingEventUtils";

/**
 * Sort field notes by date and time (descending: newest date/time first)
 * Helper function used by multiple actions
 * @param {Array} notes - Array of notes to sort
 * @returns {Array} - Sorted array of notes
 */
const sortFieldNotes = (notes) => {
  return notes.sort((a, b) => {
    const ta = getTimestamp(a.noteDate, a.fieldNoteTime);
    const tb = getTimestamp(b.noteDate, b.fieldNoteTime);
    if (tb !== ta) return tb - ta; // Newest first
    return (b.noteOrder || 0) - (a.noteOrder || 0);
  });
};

// api helpers provided by aggregator:
// - getCurrentEvent()
// - setCurrentEvent(event)
// - bump(patch?)
// - setFieldNotesUi(patchOrFn)
export const makeFieldNotesActions = (api) => ({
  // UI setters
  setActiveTab: (tab, user = null) => {
    // If switching to groupNotes, ensure a valid activeGroupId is set
    if (tab === "groupNotes") {
      const event = api.getCurrentEvent();
      const groups = event?.samplingGroups || [];

      if (groups.length > 0) {
        api.setFieldNotesUi((prev) => {
          // Check if current activeGroupId is valid
          const hasValidActiveGroup = groups.some(
            (g) => g.id === prev.activeGroupId
          );
          if (hasValidActiveGroup) {
            return { activeTab: tab };
          }

          // Find user's group or fall back to first group
          const userId = user?.employee_id;
          const userGroup = userId
            ? groups.find(
                (g) =>
                  Array.isArray(g.employees) &&
                  g.employees.some(
                    (e) => (e.employeeId ?? e.employee_id) === userId
                  )
              )
            : null;
          const defaultGroupId = (userGroup || groups[0]).id;

          return { activeTab: tab, activeGroupId: defaultGroupId };
        });
        api.bump();
        return;
      }
    }

    api.setFieldNotesUi({ activeTab: tab });
    api.bump();
  },

  setActiveGroupId: (groupId) => {
    api.setFieldNotesUi({ activeGroupId: groupId });
    api.bump();
  },

  toggleFilterMenu: () => {
    api.setFieldNotesUi((prev) => ({
      isFilterMenuOpen: !prev.isFilterMenuOpen,
    }));
    api.bump();
  },

  applyFilters: (filters) => {
    api.setFieldNotesUi((prev) => ({
      filters: { ...prev.filters, ...filters },
    }));
    api.bump();
  },

  resetFilters: () => {
    api.setFieldNotesUi((prev) => ({
      filters: { date: null, employeeId: "", groupId: "" },
    }));
    api.bump();
  },

  openModalForNew: (user) => {
    api.setFieldNotesUi({
      isNoteModalOpen: true,
      isNoteAddMode: true,
    });
    api.bump();
  },

  openModalForEdit: (note) => {
    api.setFieldNotesUi({
      isNoteModalOpen: true,
      isNoteAddMode: false,
      currentNoteId: note?.id || null,
    });
    api.bump();
  },

  closeModal: () => {
    api.setFieldNotesUi({
      isNoteModalOpen: false,
      isNoteAddMode: false,
      currentNoteId: null,
    });
    api.bump();
  },

  // Delete confirmations
  openDeleteConfirm: (note) => {
    api.setFieldNotesUi({ isDeleteConfirmOpen: true, noteToDelete: note });
    api.bump();
  },

  closeDeleteConfirm: () => {
    api.setFieldNotesUi({ isDeleteConfirmOpen: false, noteToDelete: null });
    api.bump();
  },

  // --- Utility Functions for Field Notes (migrated from fieldNotesUtils.js) ---

  /**
   * Get all field notes from the sampling event, sorted by date/time descending
   * @returns {Array} - Sorted array of all field notes
   */
  getAllFieldNotes: () => {
    const event = api.getCurrentEvent();
    if (!event || !event.fieldNotes) {
      return [];
    }
    return sortFieldNotes([...event.fieldNotes]);
  },

  /**
   * Get field notes for a specific user, sorted by date/time descending
   * @param {string} userId - The employee ID to filter by
   * @returns {Array} - Sorted array of user's field notes
   */
  getUserFieldNotes: (userId) => {
    const event = api.getCurrentEvent();
    if (!event || !event.fieldNotes || !userId) {
      return [];
    }
    const userNotes = event.fieldNotes.filter(
      (note) => note.employeeId === userId
    );
    return sortFieldNotes([...userNotes]);
  },

  /**
   * Get field notes for a specific group, sorted by date/time descending
   * @param {string} groupId - The group ID to filter by
   * @returns {Array} - Sorted array of group's field notes
   */
  getGroupFieldNotes: (groupId) => {
    const event = api.getCurrentEvent();
    if (!event || !event.fieldNotes || !groupId) {
      return [];
    }

    // Get the group to find its employees
    const group = event.samplingGroups?.find((g) => g.id === groupId);

    if (!group || !group.employees) {
      return [];
    }

    // Get the employeeIds in this group
    const employeeIds = group.employees
      .map((employee) => employee.employeeId)
      .filter((id) => id !== null);

    // Filter notes by these employee IDs
    const groupNotes = event.fieldNotes.filter((note) =>
      employeeIds.includes(note.employeeId)
    );

    return sortFieldNotes([...groupNotes]);
  },

  /**
   * Filter notes by date
   * @param {Array} notes - Array of notes to filter
   * @param {Date|string} dateFilter - Date to filter by
   * @returns {Array} - Filtered notes
   */
  filterNotesByDate: (notes, dateFilter) => {
    if (!dateFilter) {
      return notes;
    }

    const filterDateString = new Date(dateFilter).toISOString().split("T")[0];

    return notes.filter((note) => {
      if (!note.noteDate) return false;

      const noteDateString = new Date(note.noteDate)
        .toISOString()
        .split("T")[0];

      return noteDateString === filterDateString;
    });
  },

  /**
   * Filter notes by employee
   * @param {Array} notes - Array of notes to filter
   * @param {string} employeeId - Employee ID to filter by
   * @returns {Array} - Filtered notes
   */
  filterNotesByEmployee: (notes, employeeId) => {
    if (!employeeId || employeeId === "all") {
      return notes;
    }

    return notes.filter((note) => note.employeeId === employeeId);
  },

  /**
   * Filter notes by group (using employee IDs from the group)
   * @param {Array} notes - Array of notes to filter
   * @param {string} groupId - Group ID to filter by
   * @returns {Array} - Filtered notes
   */
  filterNotesByGroup: (notes, groupId) => {
    if (!groupId || groupId === "all") {
      return [];
    }

    const event = api.getCurrentEvent();
    if (!event) return notes;

    // Get the employees in this group
    const group = event.samplingGroups?.find((g) => g.id === groupId);

    if (!group || !group.employees) {
      return notes;
    }

    // Get employee IDs in the group
    const employeeIds = group.employees
      .map((employee) => employee.employeeId)
      .filter((id) => id !== null);

    // Filter notes by these employee IDs
    return notes.filter((note) => employeeIds.includes(note.employeeId));
  },

  /**
   * Apply all filters to notes
   * @param {Array} notes - Array of notes to filter
   * @param {object} filters - Object with date, employeeId, groupId filters
   * @returns {Array} - Filtered notes
   */
  applyAllFilters: (notes, filters) => {
    let filteredNotes = [...notes];
    const event = api.getCurrentEvent();

    if (filters.date) {
      const filterDateString = new Date(filters.date)
        .toISOString()
        .split("T")[0];
      filteredNotes = filteredNotes.filter((note) => {
        if (!note.noteDate) return false;
        const noteDateString = new Date(note.noteDate)
          .toISOString()
          .split("T")[0];
        return noteDateString === filterDateString;
      });
    }

    if (filters.employeeId && filters.employeeId !== "all") {
      filteredNotes = filteredNotes.filter(
        (note) => note.employeeId === filters.employeeId
      );
    }

    if (filters.groupId && filters.groupId !== "all" && event) {
      const group = event.samplingGroups?.find((g) => g.id === filters.groupId);
      if (group && group.employees) {
        const employeeIds = group.employees
          .map((employee) => employee.employeeId)
          .filter((id) => id !== null);
        filteredNotes = filteredNotes.filter((note) =>
          employeeIds.includes(note.employeeId)
        );
      }
    }

    return filteredNotes;
  },

  /**
   * Get all groups from sampling event data
   * @returns {Array} - Array of sampling groups
   */
  getAllGroups: () => {
    const event = api.getCurrentEvent();
    if (!event || !event.samplingGroups) {
      return [];
    }
    return event.samplingGroups;
  },

  /**
   * Get all unique employees who have notes
   * @returns {Array} - Array of employee objects with id and name
   */
  getUniqueEmployeesWithNotes: () => {
    const event = api.getCurrentEvent();
    if (!event || !event.fieldNotes) {
      return [];
    }

    const employeeIds = new Set();
    const employees = [];

    event.fieldNotes.forEach((note) => {
      if (note.employeeId && !employeeIds.has(note.employeeId)) {
        employeeIds.add(note.employeeId);
        employees.push({
          id: note.employeeId,
          name: note.employeeName || "Unknown",
        });
      }
    });

    return employees;
  },

  /**
   * Get next note order for a user
   * @param {string} userId - The employee ID
   * @returns {number} - The next note order number
   */
  getNextNoteOrderForUser: (userId) => {
    const event = api.getCurrentEvent();
    if (!event || !event.fieldNotes || !userId) {
      return 0;
    }

    const userNotes = event.fieldNotes.filter(
      (note) => note.employeeId === userId
    );

    if (userNotes.length === 0) {
      return 0;
    }

    // Find the highest note order and add 1
    const maxOrder = Math.max(...userNotes.map((note) => note.noteOrder || 0));
    return maxOrder + 1;
  },

  // --- Temporary State Actions for Field Notes ---
  // Mirroring the pattern for adding and updating field notes that we have in
  // our samplingEventWellsActions for the Field Measurements actions.

  /**
   * Creates the initial temporary state for the field note modal.
   * @param {object|null} existingNote - The existing note to edit (null for add mode)
   * @param {string} employeeId - The employee ID for this note
   * @param {string} employeeName - The employee name for this note
   * @returns {object} - The initial temp state
   */
  createInitialNoteTempState: (existingNote, employeeId, employeeName) => {
    if (existingNote) {
      // Edit mode - copy existing note data in camelCase format
      return {
        isNew: false,
        noteId: existingNote.id,
        data: {
          samplingEventId: existingNote.samplingEventId || "",
          noteDate: existingNote.noteDate || "",
          fieldNoteTime: existingNote.fieldNoteTime || "",
          employeeId: existingNote.employeeId || "",
          employeeName: existingNote.employeeName || "",
          noteOrder: existingNote.noteOrder ?? 0,
          fieldNote: existingNote.fieldNote || "",
        },
      };
    } else {
      // Add mode - create blank temp state with new UUID
      const newId = uuidv4();
      const currentEvent = api.getCurrentEvent();
      if (!currentEvent) return { ok: false, error: "no_event" };

      const eventId = currentEvent.id;
      const noteListLength = currentEvent.fieldNotes?.length ?? 0;

      // Getting the current date and time for the note
      const currentDate = new Date();
      const formattedDate = currentDate.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      });
      const formattedTime = currentDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      return {
        isNew: true,
        noteId: newId,
        data: {
          samplingEventId: eventId,
          noteDate: formattedDate,
          fieldNoteTime: formattedTime,
          employeeId: employeeId,
          employeeName: employeeName,
          noteOrder: noteListLength,
          fieldNote: "",
        },
      };
    }
  },

  /**
   * Commits the temporary field note state to the actual data model and server.
   * @param {object} tempState - The temporary state from the modal
   * @param {function} emitPatch - The function to emit patches to the server
   * @returns {object} - Result object with ok status
   */
  commitTempFieldNote: async (tempState, emitPatch) => {
    const event = api.getCurrentEvent();
    if (!event) {
      return { ok: false, error: "No active sampling event" };
    }

    const { isNew, noteId, data } = tempState;

    let fieldNote;

    if (isNew) {
      // Check if note already exists (prevent duplicates from double-clicks)
      const existingNote = event.fieldNotes.find((note) => note.id === noteId);

      if (existingNote) {
        // Update existing instead of creating duplicate
        fieldNote = existingNote;
      } else {
        // Create new field note - convert camelCase tempState to snake_case for constructor
        fieldNote = new EmployeeFieldNotes({
          id: noteId,
          sampling_event_id: data.samplingEventId,
          employee_id: data.employeeId,
          employee_name: data.employeeName,
          field_note: data.fieldNote,
          field_note_time: data.fieldNoteTime,
          note_order: data.noteOrder,
          note_date: data.noteDate,
        });
        event.fieldNotes.push(fieldNote);
      }
    } else {
      // Edit mode - find existing note
      fieldNote = event.fieldNotes.find((note) => note.id === noteId);
      if (!fieldNote) {
        return { ok: false, error: "Field note not found" };
      }
    }

    // Apply all data from tempState to the note using setter methods
    fieldNote.setFieldNote(data.fieldNote);
    fieldNote.setFieldNoteTime(data.fieldNoteTime);
    fieldNote.setNoteDate(data.noteDate);
    fieldNote.setNoteOrder(data.noteOrder);
    fieldNote.setEmployeeId(data.employeeId);
    fieldNote.setEmployeeName(data.employeeName);

    // Emit patch to server (upsert handles both add and update)
    const action = isNew ? "add" : "update";
    const fieldsToUpdate = [
      "fieldNote",
      "fieldNoteTime",
      "noteDate",
      "noteOrder",
      "employeeId",
      "employeeName",
    ];
    const patch = fieldNote.toPatch(action, fieldsToUpdate);

    if (typeof emitPatch === "function") {
      try {
        const result = await emitPatch(patch);
        const isSuccess =
          result === true || result?.success === true || result?.ok === true;
        if (!isSuccess) {
          // Log warning but don't fail - local state is updated
          console.warn(
            "Field note patch may not have saved:",
            result?.error || "Unknown issue"
          );
        }
      } catch (err) {
        console.error("Error emitting field note patch:", err);
        // Continue - local state is already updated
      }
    }

    api.bumpRevision();
    return { ok: true, fieldNote };
  },

  /**
   * Removes a field note from the sampling event and emits the delete patch.
   * @param {string} noteId - The ID of the note to remove
   * @param {function} emitPatch - The function to emit patches to the server
   * @returns {object} - Result object with ok status
   */
  removeFieldNote: (noteId, emitPatch) => {
    const event = api.getCurrentEvent();
    if (!event) return { ok: false, error: "no_event" };

    const fieldNote = event.fieldNotes.find((note) => note.id === noteId);
    if (!fieldNote) return { ok: false, error: "field_note_not_found" };

    // Send the patch before removing
    if (emitPatch) {
      const patch = fieldNote.toPatch("remove", []);
      emitPatch(patch);
    }

    // Remove from the event
    event.fieldNotes = event.fieldNotes.filter((note) => note.id !== noteId);

    api.bump();
    return { ok: true, fieldNote };
  },

  // Handling incoming broadcasts from the server
  /**
   * Handle incoming websocket broadcast for field note
   * @param {object} patch - The patch data with snake_case keys
   * @param {function} applyPatchToObject - Helper to apply patch using setters
   */
  handleBroadcast: (patch, applyPatchToObject) => {
    const event = api.getCurrentEvent();
    if (!event) return;

    const { id, action } = patch;
    const fieldNotes = event.fieldNotes || [];

    // Handle add action
    if (action === "add") {
      // Check if note already exists (prevent duplicates from broadcasts)
      const existingNote = fieldNotes.find((note) => note.id === id);
      if (existingNote) {
        // Update existing instead of creating duplicate
        applyPatchToObject(existingNote, patch, [
          "id",
          "action",
          "sampling_event_id",
        ]);
        api.bump();
        return;
      }
      // Create new note from patch data
      const newNote = new EmployeeFieldNotes(patch);
      fieldNotes.push(newNote);
      api.bump();
      return;
    }

    // Handle remove action
    if (action === "remove" || action === "delete") {
      const idx = fieldNotes.findIndex((note) => note.id === id);
      if (idx >= 0) {
        fieldNotes.splice(idx, 1);
        api.bump();
      }
      return;
    }

    // Handle update action - find existing note and apply patch
    const fieldNote = fieldNotes.find((note) => note.id === id);
    if (!fieldNote) {
      console.warn("handleBroadcast: field note not found for id:", id);
      return;
    }
    applyPatchToObject(fieldNote, patch, ["id", "action", "sampling_event_id"]);
    api.bump();
  },
});
